import {
  OneOfElements,
  OneOfPropertyElements,
} from '@shared/models/project.model';
import { IElementProperty } from '@shared/models/element-property.model';
import { ProjectChangeType } from '../update-engine.models';
import type { ProjectChangeMap } from '../update-engine.models';
import { cloneReplaceAt } from '@shared/helpers/array.helpers';
import {
  getElementProperties,
  setPropertyCount,
} from '../../element-property.helpers';
import { isOneOfPropertyElements } from '../../recursive-element.helpers';

type PropertyFallbackCountChange =
  ProjectChangeMap[ProjectChangeType.ElementPropertyFallbackCount];

/**
 * Build a partial element change for ElementPropertyFallbackCount (inherited fallback).
 * Updates the property's fallbackCount in element.properties.
 *
 * @param element - Current element state
 * @param change - Fallback count payload
 * @returns Partial with updated properties array, or undefined if not an element / property not found.
 *   Deep-equal no-ops are removed in `projectChangeToElementPartial`.
 */
export const elementPropertyFallbackCountToChange = (
  element: OneOfElements,
  change: PropertyFallbackCountChange,
): Partial<OneOfPropertyElements> | undefined => {
  if (!isOneOfPropertyElements(element)) return undefined;

  const props = getElementProperties(element);
  const index = props.findIndex((p) => p.name === change.propertyName);
  const prop = props[index];
  if (index === -1 || !prop) return undefined;

  // skipValidation: same rationale as element-property-count.update — pipeline transforms
  // run on potentially stale baseline options. Validation belongs at boundary.
  const updatedProp = setPropertyCount(
    prop,
    change.fallbackCount,
    'fallbackCount',
    { skipValidation: true },
  );
  return {
    properties: cloneReplaceAt(props, index, updatedProp as IElementProperty),
  };
};
